import type {
  PayPeriodTemplateCadence,
  PayPeriodTemplateStartDayOfWeek,
} from "@/business/business";

// Two kinds, because they are two different asks: the rules written out, and the numbers
// those rules were applied to. Mirrors PayrollKnowledgeDocument::ONBOARDING_DOCUMENT_TYPES
// on the API, which accepts no other value from this flow.
export const ONBOARDING_DOCUMENT_TYPE = {
  payPlan: "pay_plan",
  referenceData: "reference_data",
} as const;

export type OnboardingDocumentType =
  (typeof ONBOARDING_DOCUMENT_TYPE)[keyof typeof ONBOARDING_DOCUMENT_TYPE];

export interface DocumentExample {
  label: string;
  href: string;
  downloadHref?: string;
}

export interface DocumentCategory {
  title: string;
  stepTitle: string;
  stepSubtitle: string;
  asks: readonly string[];
  examples: readonly DocumentExample[];
}

// Each step saves only the fields it asks about. The API accepts partial updates, so
// every key is optional instead of requiring the full setup at once.
export interface SimpleBusinessSetupPayload {
  name?: string;
  time_zone_label?: string;
  st_credentials_attributes?: {
    tenant_id: string;
  };
  current_pay_period_template_attributes?: {
    cadence: PayPeriodTemplateCadence;
    start_day_of_week?: PayPeriodTemplateStartDayOfWeek;
    period_start_reference_date?: string;
  };
}

export interface OnboardingDocument {
  id: string;
  title: string;
  file_name: string;
  uploaded_at: string;
  content_type?: string | null;
  file_size?: number | null;
  // Null for anything sent before onboarding asked which kind it was, and for Volca's own
  // uploads, which use a different vocabulary.
  document_type?: string | null;
  // False for documents Volca staff added: listed, but not the customer's to delete.
  deletable: boolean;
}

export interface OnboardingDocumentsResponse {
  onboarding_documents: OnboardingDocument[];
}

export interface OnboardingDocumentResponse {
  onboarding_document: OnboardingDocument;
}

export const UPLOAD_CONFLICT_RESOLUTION = {
  replace: "replace",
  keepBoth: "keep_both",
} as const;

export type UploadConflictResolution =
  (typeof UPLOAD_CONFLICT_RESOLUTION)[keyof typeof UPLOAD_CONFLICT_RESOLUTION];

// Returned with a 409 when a document of this name already exists, so the screen can ask
// whether to replace it or keep both.
export interface UploadConflict {
  file_name: string;
  suggested_file_name: string;
  existing_document: OnboardingDocument;
}

export const UPLOAD_ITEM_STATE = {
  pending: "pending",
  uploading: "uploading",
  done: "done",
  skipped: "skipped",
  failed: "failed",
} as const;

export type UploadItemState =
  | { state: typeof UPLOAD_ITEM_STATE.pending }
  | { state: typeof UPLOAD_ITEM_STATE.uploading; progress: number }
  | { state: typeof UPLOAD_ITEM_STATE.done }
  | { state: typeof UPLOAD_ITEM_STATE.skipped }
  | { state: typeof UPLOAD_ITEM_STATE.failed; message: string };

export interface UploadItem {
  file: File;
  status: UploadItemState;
}
